import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import Chart from 'chart.js/auto';
import React from 'react';

import 'chartjs-adapter-luxon';
import useRect from 'hooks/common/useRect';

const config = (data, onComplete, duration) => ({
  type: 'scatter',
  data: data,
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        left: 70,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: 0,
        max: 24,
        grid: {
          drawTicks: false,
          drawOnChartArea: true,
          lineWidth: (context) => {
            if (context.tick.value % 6 === 0) {
              return 2;
            } else {
              return 1;
            };
          },
          display: true,
        },
        ticks: {
          min: 120,
          max: 120,
          callback: (value) => {
            if (value % 6 === 0) {
              return value;
            } else {
              return '';
            }
          },
          stepSize: 6,
          // display: false,
        },
      },
      y: {
        type: 'logarithmic',
        afterTickToLabelConversion: (scaleInstance) => {
          const ticks = scaleInstance.ticks;
          const newTicks = ticks?.filter((tick) => {
            switch (tick.value) {
              case 120: return true;
              default: return false;
            }
          });
          scaleInstance.ticks = newTicks;
        },
        min: 40,
        max: 400,
        grid: {
          drawTicks: false,
          lineWidth: 2,
          color: '#16B464',
        },
        ticks: {
          // callback: (value) => {
          //   return T('정상혈당 구간');
          // },
          font: {
            size: 11,
            family: 'Pretendard',
          },
          color: '#16B464',
          autoSkip: false,
          display: false,
        },
        // afterBuildTicks: (chart) => {
        //   chart.ticks = [{ value: 120 }];
        // },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const title = context.dataset.label
            const h = Math.floor(context.parsed.x);
            const hours = h.toString().padStart(2, '0');
            const mins = Math.floor((context.parsed.x - h) * 60).toString().padStart(2, '0');
            return T('{0} {1}:{2} ({3})', title, hours, mins, context.parsed.y);
          },
        }
      }
    },
  },
  plugins: [{
    beforeDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();

      const top = yAxis.getPixelForValue(250);
      const bottom = yAxis.getPixelForValue(54);
      ctx.fillStyle = '#F7F7F7';
      ctx.fillRect(left, top, right - left, bottom - top);

      ctx.restore();
    },
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();

      ctx.font = '12px Pretendard';
      ctx.fillStyle = '#aaaaaa';
      const y = yAxis.getPixelForValue(1500);

      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(T('혈당'), left - 3, y + 15);
      ctx.fillText(T('({0})', 'mg/dL'), left - 3, y + 30);


      const top = yAxis.getPixelForValue(250);
      const bottom = yAxis.getPixelForValue(54);
      ctx.fillStyle = '#AAAAAA';
      ctx.textBaseline = 'middle';
      ctx.fillText('250', left - 3, top);
      ctx.fillText('54', left - 3, bottom);

      const y120 = yAxis.getPixelForValue(120);
      const text = T('정상혈당 구간');
      const metrics = ctx.measureText(text)
      const { width } = metrics;
      const height = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

      ctx.strokeStyle = '#16B464';
      ctx.beginPath()
      ctx.moveTo(left - 5, y120);
      ctx.lineTo(right, y120);
      ctx.stroke();

      ctx.fillStyle = '#16B464';
      ctx.fillRect(left - 3 - width, y120 - height * 0.5 - 2, width - 5, height + 3);
      ctx.beginPath()
      ctx.moveTo(left - 4, y120);
      ctx.lineTo(left - 8, y120 - height * 0.5 - 2);
      ctx.lineTo(left - 8, y120 + height * 0.5 + 1);
      ctx.lineTo(left - 4, y120);
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = '10px Pretendard';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, left - 10, y120);

      ctx.restore();
    }
  }]
});

export default function AbnormalBloodGlocoseBoxChart(props) {
  const {
    renderAll,
    duration,
    onComplete,
    list = [],
    ...rest
  } = props || {};

  const data = React.useMemo(() => {
    const low = [];
    const high = [];

    list.forEach(datum => {
      const {
        bld_sugr_time,
        bld_sugr_value: y,
        bld_sugr_tp,
      } = datum;

      if (bld_sugr_tp === 'H') {
        const hour = +bld_sugr_time.substring(0, 2);
        const min = +bld_sugr_time.substring(2, 4);
        const sec = +bld_sugr_time.substring(4, 6);
        const x = (sec + min * 60 + hour * 60 * 60) / (60 * 60 * 24) * 24;
        high.push({
          rmin: 1,
          rmax: 1,
          x,
          y,
        });
      } else if (bld_sugr_tp === 'L') {
        const hour = +bld_sugr_time.substring(0, 2);
        const min = +bld_sugr_time.substring(2, 4);
        const sec = +bld_sugr_time.substring(4, 6);
        const x = (sec + min * 60 + hour * 60 * 60) / (60 * 60 * 24) * 24;
        low.push({
          rmin: 1,
          rmax: 1,
          x,
          y,
        });
      }
    });

    return {
      labels: ['0', null, null, '6', null, null, '12', null, null, '18', null, null, '24'],
      datasets: [{
        label: T('저혈당'),
        data: low,
        borderColor: '#5100FF',
        backgroundColor: '#5100FF80',
      }, {
        label: T('고혈당'),
        data: high,
        borderColor: '#FF995A',
        backgroundColor: '#FF995A80',
      }],
    }
  }, [list]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(data, onComplete, renderAll ? 0 : duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const configuration = config(data, onComplete, renderAll ? 0 : duration);
    configuration.data.datasets.forEach((dataset, datasetIndex) => {
      dataset.data.forEach((datum, datumIndex) => {
        chartRef.current.config.data.datasets[datasetIndex].data[datumIndex] = datum;
      });
      chartRef.current.config.data.datasets[datasetIndex].data.splice(dataset.data.length, chartRef.current.config.data.datasets[datasetIndex].data.length - dataset.data.length);
    });
    chartRef.current.update();
  }, [data, duration, onComplete, renderAll]);

  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div
      {...ex(['Chart', 'AbnormalBloodGlocoseBoxChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}
